import type { CheckoutV2Configuration } from "@jti/commerce";
import type { TelephoneFieldValue } from "@jti/commerce-types";
import type { Config } from "@jti/fe-common";
import type { GetProfileQueryData } from "@jti/store/slices/profileApiSlice";
import type { TelephoneProps } from "@jti/ui";

import type { FormFieldConfig, FormFieldFactory } from "@checkout-v2/formFields";
// eslint-disable-next-line no-restricted-imports
import {
  getSavedTelephoneDefaultValue,
  toTelephoneString,
} from "@checkout-v2/formFields/factories/telephoneFieldFactory/util";
import {
  checkIsEmptyTelephoneField,
  toEmptyTelephoneField,
  toTelephoneField,
} from "@checkout-v2/shared/utils/telephoneUtils/toTelephoneField";

export type TelephoneFieldGetDefaultValueArgs = {
  checkoutConfig: CheckoutV2Configuration;
  config: Config;
  profile: GetProfileQueryData;
};

export type TelephoneFieldGetConfigArgs = TelephoneFieldGetDefaultValueArgs;

export type TelephoneFieldConfig = FormFieldConfig<
  "telephone",
  Pick<TelephoneProps, "fieldName" | "isRequired"> & {
    savedDefaultValue: TelephoneFieldValue;
    isPhoneNumberValid: boolean;
  }
>;

type TelephoneFieldFactory = FormFieldFactory<
  "telephone",
  TelephoneFieldValue,
  [TelephoneFieldGetConfigArgs],
  [TelephoneFieldGetDefaultValueArgs],
  TelephoneFieldConfig
> & { toTelephoneString: typeof toTelephoneString; toEmptyTelephoneField: typeof toEmptyTelephoneField };

export const telephoneFieldFactory: TelephoneFieldFactory = {
  getDerivedConfig: ({ config, checkoutConfig, profile }) => {
    const isPhoneNumberValid =
      !profile.personalInfo.telephone ||
      !checkIsEmptyTelephoneField(
        toTelephoneField(
          profile.personalInfo.telephone,
          config.isInternationalPhoneEnabled,
          config.TELEPHONE_CODES_LIST
        )
      );

    return {
      name: "telephone",
      isRequired: /true/i.test(config.CHECKOUT_PHONE_REQUIRED),
      isHidden: !checkoutConfig.contact?.populatedPhoneInput && isPhoneNumberValid,
      isPhoneNumberValid,
      fieldName: (name) => `telephone.${name}`,
      savedDefaultValue: getSavedTelephoneDefaultValue(
        profile.personalInfo.telephone,
        config.isInternationalPhoneEnabled,
        config.TELEPHONE_CODES_LIST
      ),
    };
  },
  getDefaultValue: ({ config, checkoutConfig, profile }) => {
    return getSavedTelephoneDefaultValue(
      profile.personalInfo.telephone,
      config.isInternationalPhoneEnabled,
      config.TELEPHONE_CODES_LIST,
      !!checkoutConfig.contact?.populatedPhoneInput
    );
  },
  toTelephoneString,
  toEmptyTelephoneField,
};
